import React from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';

/**
 * Top-of-page banner shown when an admin has elevated into another user's
 * account via /admin/users/{id}/impersonate.  Provides a one-click "exit"
 * that restores the original admin token from localStorage.
 *
 * The banner is rendered globally from App.js so it's visible on every route
 * the impersonated user navigates to.
 */
export default function ImpersonationBanner() {
  const backupToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token_backup') : null;
  const impersonatorEmail = typeof window !== 'undefined' ? localStorage.getItem('impersonator_email') : null;
  const currentToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  if (!backupToken || !currentToken) return null;

  // Decode the current token to read who we're impersonating.
  let impersonatedAs = '';
  let isImpersonation = false;
  try {
    const payload = JSON.parse(atob(currentToken.split('.')[1] || ''));
    if (payload?.impersonator) {
      isImpersonation = true;
      impersonatedAs = payload.email || '';
    }
  } catch {
    // Malformed token — clear backup so we don't loop the banner.
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('impersonator_email');
    return null;
  }
  if (!isImpersonation) {
    // Token is no longer the impersonation token (admin signed back in normally) — clean up.
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('impersonator_email');
    return null;
  }

  const exitImpersonation = () => {
    localStorage.setItem('token', backupToken);
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('impersonator_email');
    window.location.href = '/platform/admin';
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 flex items-center justify-between gap-3 shadow-md"
      style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="flex items-center gap-2 min-w-0">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        <span className="text-[12px] font-bold truncate">
          ADMIN MODE — Signed in as <span className="underline">{impersonatedAs}</span>
          {impersonatorEmail && <span className="hidden sm:inline opacity-90"> · originally {impersonatorEmail}</span>}
        </span>
      </div>
      <button
        onClick={exitImpersonation}
        data-testid="impersonation-exit"
        className="flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-bold bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all shrink-0">
        <LogOut className="w-3 h-3" /> Exit
      </button>
    </div>
  );
}
